<template>
	<Modal
		v-if="!isLoggedIn"
		modal-name="KroneLoginModal"
		class="krone-login__modal"
		type="dialog"
		:theme="$arenaConfig.isLightTheme ? null : 'dark'"
		:closeable="false"
	>
		<template #title>
			<span data-testid="krone-login__title">{{ $I18n.trans('payments.krone.modal_title') }}</span>
		</template>
		<template #content>
			<div class="krone-login__desc">
				{{ $I18n.trans(`payments.krone.content_logged_out`) }}
			</div>
			<div
				class="krone-login__hint"
			>
				<V2Icon
					icon-name="c-info"
					size="md"
					class="krone-login__hint-icon"
				/>
				{{ $I18n.trans('payments.krone.login_hint') }}
			</div>
		</template>
		<template #footer>
			<div class="l-button-group">
				<Button
					v-if="backUrl && !$Helpers.isSmartTV()"
					class="krone-login__back-button"
					:text="$Helpers.ucfirst($I18n.trans('commons.back'))"
					theme="dark"
					modifier="secondary"
					@click="back()"
				/>
				<Button
					:text="$I18n.trans('arena.login.error_page.button')"
					theme="dark"
					autofocus
					@click="next()"
				/>
			</div>
		</template>
	</Modal>
	<div
		v-else-if="iFrameVisible"
		class="krone-payment krone-payment--small"
		role="dialog"
		aria-modal="true"
		aria-label="Payment"
	>
		<div class="krone-payment__backdrop" />
		<div class="krone-payment__content">
			<iframe
				ref="krnIframe"
				class="krone-payment__iframe"
				:src="iframeSrc"
				:style="iframeStyle"
				allow="fullscreen;payment"
			/>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import Modal from '@shared/components/Modal';
import Button from '@shared/components/Button.vue';
import { httpClient } from '@shared/js/api/httpClient';
import laroute from '@laroute';

const MIN_IFRAME_HEIGHT_PX = 654;
const IFRAME_MESSAGES = ['krn-iframe-height', 'krn_paywall'];

export default {
	name:       'KronePaymentModal',
	components: { Button, Modal },
	props:      {
		event: {
			type:     Object,
			required: true,
		},
	},
	data() {
		return {
			iFrameVisible: false,
			externalOffer: null,
			iframeStyle:   {
				height: `${MIN_IFRAME_HEIGHT_PX}px`,
			},
			lastSetHeightPx: MIN_IFRAME_HEIGHT_PX,
		};
	},
	computed: {
		...mapState({
			profileIsInitialized: state => state.user.isInitialized,
		}),
		...mapGetters({
			hasAccessToEvent: 'user/hasAccessToEvent',
			isLoggedIn:       'user/isLoggedIn',
		}),
		iframeSrc() {
			if (this.externalOffer) {
				return `${this.externalOffer.externalUrl}?returnUrl=${this.event?.url}&postMessage=1&KRN_FORCE_FAM=desktop`;
			}
			// Default fallback
			return `https://www.krone.at/sport-frame.html?returnUrl=${this.event?.url}&postMessage=1&KRN_FORCE_FAM=desktop`;
		},
		backUrl() {
			return $Helpers.getBackUrl();
		},
	},
	watch: {
		profileIsInitialized() {
			if (!this.hasAccessToEvent(this.event) || !this.isLoggedIn) {
				this.initializeModal();
			}
		},
	},
	mounted() {
		window.addEventListener('message', this.onMessage, false);
		// If profile state is already known on mount, ensure the correct UI is shown
		if (this.profileIsInitialized && (!this.hasAccessToEvent(this.event) || !this.isLoggedIn)) {
			this.initializeModal();
		}
	},
	beforeDestroy() {
		window.removeEventListener('message', this.onMessage, false);
	},
	methods: {
		async initializeModal() {
			if (this.isLoggedIn) {
				await this.getPaymentOffers();
				// Logged in but no access to the event, show the external payment form
				this.iFrameVisible = true;
			} else {
				// Not logged in, show the login modal
				this.eventBus.$emit('modal:KroneLoginModal');
			}
		},
		async getPaymentOffers() {
			const response = await httpClient.getPaymentOffers({ eventId: this.event.id });
			const paymentOffers = Array.isArray(response.data) ? response.data : [];

			// Use the first payment offer that has an external URL
			this.externalOffer = paymentOffers.find(offer => offer.isExternal && !!offer.externalUrl);
		},
		onMessage(event) {
			const data = event.data;

			// Ignore all other messages
			if (!data || !IFRAME_MESSAGES.includes(data.type)) {
				return;
			}

			if (data.type === 'krn_paywall') {
				this.handlePaywallData(data);
			} else {
				this.handleIframeHeight(data);
			}
		},
		handlePaywallData() {
			// Payment canceled or went through
			window.location.reload();
		},
		handleIframeHeight(data) {
			const reported = Number(data.height);
			if (!Number.isFinite(reported) || reported <= 0) return;

			// Round and clamp
			const desired = Math.max(MIN_IFRAME_HEIGHT_PX, Math.round(reported));

			// Hysteresis + throttle
			const current = this.lastSetHeightPx || MIN_IFRAME_HEIGHT_PX;

			// Only resize if difference is significant
			const GROW_THRESHOLD = 20;
			const SHRINK_THRESHOLD = 20;

			const growEnough = desired >= current + GROW_THRESHOLD;
			const shrinkEnough = desired <= current - SHRINK_THRESHOLD;

			if (growEnough || shrinkEnough) {
				this.iframeStyle.height = `${desired}px`;
				this.lastSetHeightPx = desired;
			}
		},
		back() {
			// Back to the previous page
			window.location.href = this.backUrl;
		},
		next() {
			// Redirect to SSO login page
			window.location.href = laroute.route('arena.login', { redirectUrl: window.location.href });
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';
/* stylelint-disable */

// Login Modal

.krone-login__modal {
	::v-deep {
		.v2-modal__content-w {
			border-radius: $sp4 !important;

			@include mobile-media-query {
				border-radius: $sp4 $sp4 0 0 !important;
			}
		}
	}
}

.krone-login__desc {
	margin-bottom: $sp6;
}

.krone-login__hint {
	margin-top: $sp5;
	position: relative;
	color: var(--color-grey-300);
	padding-left: 30px;

	&-icon {
		position: absolute;
		top: 0;
		left: 0;
	}
}

// External Modal
.krone-payment {
	position: fixed;
	inset: 0;
	z-index: 10000;
}

.krone-payment__backdrop {
	position: absolute;
	inset: 0;
	opacity: 0.8;
	background-color: var(--color-black);
}

.krone-payment__content {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	background: #111;
	border-radius: 10px;
	overflow: hidden;
	width: min(95vw, 1000px);
	max-height: calc(100vh - 16px);
}

.krone-payment__iframe {
	display: block;
	width: 100%;
	border: 0;
	// Helps avoid scrollbar-induced feedback in some browsers when content is near the boundary
	// Note: overflow on iframe is not standardized, but this avoids outlines/scrollbars where applicable
	outline: none;
	height: auto;
	max-height: calc(100vh + 24px + 33px); // account for negative margin from top and bottom
	margin-top: -24px;
	margin-bottom: -33px;
	transition: height 10ms ease-in-out;
}

/* stylelint-enable */
</style>
